import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useRegistration } from '../store/RegistrationContext';
import { registrationActionTypes } from '../store/registrationReducer';
import retreatService from '../services/retreatService';

export default function RetreatLandingPage() {
  const navigate = useNavigate();
  const { dispatch } = useRegistration();
  const [centers, setCenters] = useState([]);
  const [selectedCenter, setSelectedCenter] = useState(null);
  const [camps, setCamps] = useState([]);
  const [selectedCamp, setSelectedCamp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState(1);

  useEffect(() => {
    fetchCenters();
  }, []);

  const fetchCenters = async (country = 'USA') => {
    try {
      setLoading(true);
      const data = await retreatService.getCenters(country);
      console.log('Centers API response:', data);
      setCenters(data || []);
    } catch (error) {
      console.error('Error fetching centers:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCenterSelect = async (center) => {
    setSelectedCenter(center);
    dispatch({ type: registrationActionTypes.SELECT_CENTER, payload: center.center_Id });
    
    try {
      setLoading(true);
      const campData = await retreatService.getCampsByCenter(center.center_Id);
      setCamps(campData || []);
      setStep(2);
    } catch (error) {
      console.error('Error fetching camps:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCampSelect = (camp) => {
    setSelectedCamp(camp);
  };

  const handleNext = () => {
    if (selectedCamp) {
      dispatch({ type: registrationActionTypes.SELECT_RETREAT, payload: selectedCamp.camp_Id });
      navigate('/login', { state: { from: '/students' } });
    }
  };

  const handleBack = () => {
    setStep(1);
    setSelectedCenter(null);
    setCamps([]);
    setSelectedCamp(null);
  };

  return (
    <div className="relative min-h-screen bg-gradient-to-br from-indigo-50/70 via-white to-rose-50/70 pb-24">
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -top-24 -left-24 h-72 w-72 rounded-full bg-purple-200/35 blur-3xl"></div>
        <div className="absolute top-10 -right-24 h-72 w-72 rounded-full bg-blue-200/35 blur-3xl"></div>
        <div className="absolute bottom-0 left-1/3 h-80 w-80 rounded-full bg-rose-200/25 blur-3xl"></div>
      </div>

      <div className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">
          <div className="overflow-hidden rounded-[28px] bg-white/75 backdrop-blur-xl shadow-[0_26px_70px_-45px_rgba(15,23,42,0.55)] border border-white/60 mb-4">
            <div className="bg-gradient-to-br from-white via-slate-50 to-violet-50 px-6 py-5 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-2xl font-bold text-gray-900">
                    {step === 1 ? 'Which Retreat?' : selectedCenter?.center_Name}
                  </div>
                  <div className="text-sm text-gray-600 mt-1">
                    {step === 1 ? 'Select a center to view available retreats' : `Step - ${step}`}
                  </div>
                </div>
                {step === 2 && (
                  <button
                    onClick={handleBack}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-red-500 text-white text-sm font-semibold rounded-lg hover:bg-red-600 transition-colors"
                  >
                    <i className="fa-solid fa-arrow-left"></i>
                    Back
                  </button>
                )}
              </div>
            </div>

            <div className="p-6 md:p-8">
              {loading ? (
                <div className="text-center py-12">
                  <div className="animate-spin rounded-full h-14 w-14 border-b-4 border-indigo-600 mx-auto mb-4"></div>
                  <p className="text-gray-600">Loading...</p>
                </div>
              ) : step === 1 ? (
                <div className="space-y-6">
                  {centers.map((center) => (
                    <div
                      key={center.center_Id}
                      className="rounded-2xl bg-white border border-gray-200 shadow-sm overflow-hidden hover:shadow-lg transition-shadow cursor-pointer"
                      onClick={() => handleCenterSelect(center)}
                    >
                      <div className="flex flex-col md:flex-row">
                        <div className="md:w-48 h-48 md:h-auto flex-shrink-0">
                          <img
                            src={center.center_Logo_URL}
                            alt={center.center_Name}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              e.currentTarget.src = 'https://via.placeholder.com/300x200?text=Center+Image';
                            }}
                          />
                        </div>
                        <div className="flex-1 p-6">
                          <div className="flex items-start justify-between gap-4">
                            <div>
                              <h3 className="text-lg font-bold text-gray-900 mb-2">
                                {center.center_Name}
                              </h3>
                              <p className="text-sm text-gray-600 line-clamp-3">
                                {center.center_desc || 'No description available'}
                              </p>
                            </div>
                            {center.camp_Count > 0 && (
                              <span className="inline-flex items-center gap-1 px-3 py-1 bg-green-100 text-green-800 rounded-full text-xs font-bold">
                                {center.camp_Count} {center.camp_Count === 1 ? 'Camp' : 'Camps'}
                              </span>
                            )}
                          </div>
                          <div className="mt-4">
                            <button className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-indigo-600 via-purple-600 to-amber-500 text-white text-sm font-semibold rounded-lg hover:shadow-lg transition-shadow">
                              View Camps
                              <i className="fa-solid fa-arrow-right"></i>
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div>
                  <div className="mb-6 rounded-2xl bg-gradient-to-br from-blue-50 to-purple-50 border border-blue-200 p-4">
                    <p className="text-sm text-gray-700">
                      Need some information about the retreat you are interested in ...
                    </p>
                  </div>

                  <div className="space-y-4">
                    {camps.length > 0 ? (
                      camps.map((camp) => {
                        const isSelected = selectedCamp?.camp_Id === camp.camp_Id;
                        return (
                          <div
                            key={camp.camp_Id}
                            onClick={() => handleCampSelect(camp)}
                            className={`rounded-2xl border-2 p-6 cursor-pointer transition-all ${
                              isSelected
                                ? 'border-amber-400 bg-gradient-to-br from-amber-50 to-yellow-50 shadow-lg'
                                : 'border-gray-200 bg-white hover:border-gray-300 hover:shadow-md'
                            }`}
                          >
                            <div className="flex items-start justify-between gap-4">
                              <div className="flex-1">
                                <div className="flex items-center gap-3 mb-2">
                                  <div className="w-16 h-16 rounded-full bg-gradient-to-br from-indigo-600 via-purple-600 to-amber-500 flex items-center justify-center text-white shadow-md">
                                    <i className="fa-solid fa-campground text-2xl"></i>
                                  </div>
                                  <div>
                                    <h3 className="text-lg font-bold text-gray-900">
                                      {camp.camp_Name || 'Spiritual Retreat'}
                                    </h3>
                                    <p className="text-sm text-gray-600">
                                      {camp.camp_Start_Date && camp.camp_End_Date
                                        ? `${new Date(camp.camp_Start_Date).toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: 'numeric' })} - ${new Date(camp.camp_End_Date).toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: 'numeric' })}`
                                        : 'Dates TBA'}
                                    </p>
                                  </div>
                                </div>
                                {camp.camp_Description && (
                                  <p className="text-sm text-gray-600 mt-2">
                                    {camp.camp_Description}
                                  </p>
                                )}
                              </div>
                              {isSelected && (
                                <div className="flex-shrink-0">
                                  <div className="w-12 h-12 rounded-full bg-gradient-to-r from-indigo-600 via-purple-600 to-amber-500 flex items-center justify-center text-white shadow-lg">
                                    <i className="fa-solid fa-check text-xl"></i>
                                  </div>
                                </div>
                              )}
                            </div>
                            <div className="mt-4 pt-4 border-t border-gray-200">
                              <div className="text-xs font-semibold text-gray-500">
                                {isSelected ? 'Selected' : 'Click to select this retreat'}
                              </div>
                            </div>
                          </div>
                        );
                      })
                    ) : (
                      <div className="text-center py-12 bg-white/70 rounded-2xl border border-gray-200">
                        <i className="fa-solid fa-campground text-gray-300 text-5xl mb-4"></i>
                        <p className="text-gray-700 text-sm font-semibold mb-1">No camps available</p>
                        <p className="text-gray-500 text-xs">Please check back later</p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {step === 2 && selectedCamp && (
        <div className="fixed bottom-0 left-0 right-0 bg-white/90 backdrop-blur-xl border-t border-gray-200 shadow-lg z-40">
          <div className="container mx-auto px-4 py-3">
            <div className="max-w-4xl mx-auto flex items-center justify-center">
              <button
                onClick={handleNext}
                className="inline-flex items-center gap-2 px-8 py-3 bg-gradient-to-r from-green-600 to-emerald-600 text-white font-semibold rounded-xl hover:shadow-xl transition-all duration-200 text-lg"
              >
                Next
                <i className="fa-solid fa-arrow-right"></i>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
